import api from './api';

/** 
 * Collaboration Service
 * Handles collaboration rooms and team collaboration
 */
export const collaborationService = {
  // ==================== Room Management ====================
  
  /**
   * Get all collaboration rooms
   */
  async getRooms(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/collaboration/rooms?${queryString}`);
  },

  async getMyRooms() {
    return api.get('/collaboration/rooms/my-rooms');
  },

  async getPendingInvitations() {
    return api.get('/collaboration/rooms/invitations/pending');
  },

  async getConnections(userId) {
    return api.get(`/connections/user/${userId}?page=0&size=200`);
  },

  async acceptInvitation(roomId) {
    return api.post(`/collaboration/rooms/${roomId}/participants/accept`);
  },

  async declineInvitation(roomId) {
    return api.post(`/collaboration/rooms/${roomId}/participants/decline`);
  },

  /**
   * Get room details
   */
  async getRoomDetails(roomId) {
    return api.get(`/collaboration/rooms/${roomId}`);
  },

  /**
   * Create collaboration room
   */
  async createRoom(roomData) {
    return api.post('/collaboration/rooms', roomData);
  },

  /**
   * Update room
   */
  async updateRoom(roomId, roomData) {
    return api.put(`/collaboration/rooms/${roomId}`, roomData);
  },

  /**
   * Delete room
   */
  async deleteRoom(roomId) {
    return api.delete(`/collaboration/rooms/${roomId}`);
  },

  /**
   * Archive room
   */
  async archiveRoom(roomId) {
    return api.post(`/collaboration/rooms/${roomId}/archive`);
  },

  // ==================== Room Messages ====================
  
  /**
   * Get room messages
   */
  async getMessages(roomId, params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/collaboration/rooms/${roomId}/messages?${queryString}`);
  },

  /**
   * Post message to room
   */
  async postMessage(roomId, messageData) {
    return api.post(`/collaboration/rooms/${roomId}/messages`, messageData);
  },

  /**
   * Update message
   */
  async updateMessage(roomId, messageId, messageData) {
    return api.put(`/collaboration/rooms/${roomId}/messages/${messageId}`, messageData);
  },

  /**
   * Delete message
   */
  async deleteMessage(roomId, messageId) {
    return api.delete(`/collaboration/rooms/${roomId}/messages/${messageId}`);
  },

  /**
   * Add reaction to message
   */
  async addReaction(roomId, messageId, emoji) {
    return api.post(`/collaboration/rooms/${roomId}/messages/${messageId}/reactions`, { emoji });
  },

  /**
   * Remove reaction from message
   */
  async removeReaction(roomId, messageId, reactionId) {
    return api.delete(`/collaboration/rooms/${roomId}/messages/${messageId}/reactions/${reactionId}`);
  },

  // ==================== Room Participants ====================
  
  /**
   * Get room participants
   */
  async getParticipants(roomId) {
    return api.get(`/collaboration/rooms/${roomId}/participants`);
  },

  /**
   * Invite participant
   */
  async inviteParticipant(roomId, userData) {
    return api.post(`/collaboration/rooms/${roomId}/participants`, userData);
  },

  /**
   * Invite a specific participant with an invitation message
   */
  async inviteParticipantToRoom(roomId, userData) {
    return api.post(`/collaboration/rooms/${roomId}/participants/invite`, userData);
  },

  /**
   * Bulk invite participants
   */
  async bulkInviteParticipants(roomId, userIds) {
    return api.post(`/collaboration/rooms/${roomId}/participants/bulk`, { userIds });
  },

  /**
   * Update participant permissions
   */
  async updateParticipantPermissions(roomId, participantId, permissions) {
    return api.put(`/collaboration/rooms/${roomId}/participants/${participantId}`, permissions);
  },

  /**
   * Remove participant
   */
  async removeParticipant(roomId, participantId) {
    return api.delete(`/collaboration/rooms/${roomId}/participants/${participantId}`);
  },

  /**
   * Leave room
   */
  async leaveRoom(roomId) {
    return api.post(`/collaboration/rooms/${roomId}/leave`);
  },

  // ==================== Room Insights ====================
  
  /**
   * Get room insights
   */
  async getInsights(roomId, params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/collaboration/rooms/${roomId}/insights?${queryString}`);
  },

  /**
   * Post insight
   */
  async postInsight(roomId, insightData) {
    return api.post(`/collaboration/rooms/${roomId}/insights`, insightData);
  },

  /**
   * Update insight
   */
  async updateInsight(roomId, insightId, insightData) {
    return api.put(`/collaboration/rooms/${roomId}/insights/${insightId}`, insightData);
  },

  /**
   * Delete insight
   */
  async deleteInsight(roomId, insightId) {
    return api.delete(`/collaboration/rooms/${roomId}/insights/${insightId}`);
  },

  /**
   * Like an insight
   */
  async likeInsight(roomId, insightId) {
    return api.post(`/collaboration/rooms/${roomId}/insights/${insightId}/like`);
  },

  // ==================== Room Activity ====================
  
  /**
   * Get room activity
   */
  async getRoomActivity(roomId, params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/collaboration/rooms/${roomId}/activity?${queryString}`);
  },

  /**
   * Get room statistics
   */
  async getRoomStatistics(roomId) {
    return api.get(`/collaboration/rooms/${roomId}/stats`);
  },

  // ==================== Search ====================
  
  /**
   * Search rooms
   */
  async searchRooms(searchParams) {
    return api.post('/collaboration/rooms/search', searchParams);
  },

  /**
   * Search messages in room
   */
  async searchMessages(roomId, query) {
    return api.get(`/collaboration/rooms/${roomId}/messages/search?q=${encodeURIComponent(query)}`);
  },
};

export default collaborationService;
